import { AppLayout } from "@/components/AppLayout";
import { Building2, Rocket, Sparkles, MapPin, Mail, ChevronRight } from "lucide-react";

const INSPIRATIONS = [
  { name: "Cliff Asness",  org: "AQR Capital Management", note: "Quantitative Research & Systematic Investing", color: "#8AE234" },
  { name: "David Siegel",  org: "Two Sigma",              note: "Data, Technology & Quantitative Investment",   color: "#00E5FF" },
  { name: "John Overdeck", org: "Two Sigma",              note: "Mathematics, Statistics & Quantitative Investment", color: "#A855F7" },
  { name: "Ken Griffin",   org: "Citadel",                note: "Trading, Technology, Market Making & Risk Management", color: "#EAB308" },
];

export default function About() {
  return (
    <AppLayout>
      <div className="space-y-8 max-w-5xl mx-auto" data-testid="about-page">
        {/* Hero */}
        <div className="nb-card p-6 md:p-10 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#8AE234]/10 blur-3xl pointer-events-none"/>
          <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none"/>
          <div className="relative">
            <div className="text-[10px] tracking-widest text-[#8AE234] font-bold uppercase mb-3">Our Journey · Research · Technology · Trust</div>
            <h1 className="font-heading text-white text-3xl md:text-5xl font-bold leading-tight">Next Billion.world</h1>
            <div className="font-heading text-[#8AE234] text-2xl md:text-4xl font-bold leading-tight">Built for the next era.</div>
            <p className="text-slate-300 mt-4 max-w-3xl">
              Next Billion.world is a technology-driven Algorithmic Trading Platform built around
              Market Research, Quantitative Analysis, Trading Technology, Data Analysis and Risk Management.
            </p>
          </div>
        </div>

        {/* Timeline */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="nb-card p-6 border border-cyan-500/40" data-testid="about-research-start">
            <div className="flex items-center gap-2 text-xs text-cyan-400 uppercase tracking-widest font-bold">
              <Rocket size={14}/> Beginning of Research
            </div>
            <div className="font-heading text-white text-3xl mt-2">Jan 15, 2026</div>
            <p className="text-slate-300 text-sm mt-2">
              Research &amp; technology development began — focused on market behavior, quantitative research,
              algorithmic trading, data analysis and risk management.
            </p>
          </div>
          <div className="nb-card p-6 border border-[#8AE234]/40" data-testid="about-launch">
            <div className="flex items-center gap-2 text-xs text-[#8AE234] uppercase tracking-widest font-bold">
              <Sparkles size={14}/> International Launch
            </div>
            <div className="font-heading text-white text-3xl mt-2">Jul 28, 2026</div>
            <p className="text-slate-300 text-sm mt-2">
              After research and technological preparation, Next Billion.world began its international
              platform launch, opening institutional-grade tools to retail investors worldwide.
            </p>
          </div>
        </div>

        {/* Research Inspiration */}
        <div className="nb-card p-6" data-testid="about-inspirations">
          <div className="text-xs text-[#EAB308] uppercase tracking-widest font-bold mb-2">Research Inspiration</div>
          <p className="text-slate-300 text-sm mb-5">
            Our research framework is inspired by the contributions of prominent figures in modern
            Quantitative Finance and Systematic Trading:
          </p>
          <div className="space-y-3">
            {INSPIRATIONS.map((p) => (
              <div key={p.name} className="flex items-start gap-3">
                <div className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{background: p.color}}/>
                <div>
                  <div className="text-white font-semibold" style={{color: p.color}}>{p.name}</div>
                  <div className="text-slate-300 text-sm">
                    <span className="text-slate-400">{p.org}</span> <ChevronRight size={12} className="inline text-slate-600"/> {p.note}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-xs text-slate-500 italic mt-5 border-t border-white/5 pt-4">
            Note: These individuals are not affiliated with Next Billion.world; their research and contributions serve only as sources of inspiration.
          </div>
        </div>

        {/* Our Technology */}
        <div className="nb-card p-6" data-testid="about-tech">
          <div className="text-xs text-cyan-400 uppercase tracking-widest font-bold mb-2">Our Technology</div>
          <p className="text-slate-300 text-sm">
            Four specialized Trading Engines have been developed through research and testing to analyze
            market data, trends, patterns, quantitative signals and potential risks — powering every trade
            you see on Next Billion.world.
          </p>
        </div>

        {/* Research Center */}
        <div className="nb-card p-6 border border-[#EAB308]/40" data-testid="about-hq">
          <div className="flex items-start gap-3">
            <div className="p-3 rounded-xl bg-[#EAB308]/10 border border-[#EAB308]/30">
              <Building2 size={22} className="text-[#EAB308]"/>
            </div>
            <div className="flex-1">
              <div className="text-xs text-[#EAB308] uppercase tracking-widest font-bold">Research &amp; Technology Center</div>
              <div className="text-white font-heading text-lg mt-1">Sanford Quantitative Trading Research Center (SQTRC)</div>
              <div className="text-slate-300 text-sm mt-2 flex items-start gap-2">
                <MapPin size={14} className="text-slate-500 mt-0.5 shrink-0"/>
                <span>500 Westover Dr PMB 9816, Sanford, North Carolina 27330, USA</span>
              </div>
              <div className="text-slate-300 text-sm mt-1 flex items-center gap-2">
                <Mail size={14} className="text-slate-500 shrink-0"/>
                <a href="mailto:support@nextbillion.world" className="text-cyan-300 hover:underline">support@nextbillion.world</a>
              </div>
            </div>
          </div>
        </div>

        {/* Philosophy */}
        <div className="nb-card p-6 border border-[#8AE234]/50 bg-[#8AE234]/5" data-testid="about-philosophy">
          <div className="text-xs text-[#8AE234] uppercase tracking-widest font-bold">Our Philosophy</div>
          <div className="text-white font-heading text-lg md:text-xl mt-2">
            Research &nbsp;+&nbsp; Technology &nbsp;+&nbsp; Data &nbsp;+&nbsp; Risk Management
          </div>
          <div className="text-[#8AE234] font-heading text-xl md:text-2xl font-bold mt-1">
            = Intelligent Trading Infrastructure
          </div>
          <div className="text-slate-400 italic text-sm mt-3">
            Built on Research. Powered by Technology. Designed for the Future of Trading.
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
